
import {createDialogOverlay, fillDialog, pvpButton, markDigitizationUI} from "../DialogHelpers.js";
import {hostDialogAvailable, requestHostConfirm, htmlMessageToText} from "../HostDialog.js";
import {renderProfileChartCanvas} from "./ProfileChartRenderer.js";
import {buildCrossSectionPdf} from "./CrossSectionPdfBuilder.js";
import {formatNumber, formatGeneratedAt} from "./reportFormat.js";
import {REPORT_LOGO_DATA_URI} from "./reportLogo.js";

import {t} from "../i18n/index.js";
// Result panel for "Tværprofiler" (task sections 8-12) - centred modal on the shared
// Potree dialog skin (DialogHelpers.js; close via button/Escape/click-outside), just larger, since the content
// here is a full multi-part report rather than a short text summary.
//
// This is now purely an ON-SCREEN preview - the previous iteration made the
// report body double as the literal print source via window.print(); that
// approach is gone (see CrossSectionPdfBuilder.js's own doc comment for
// why: browser print header/footer can't be suppressed, and the modal's own
// scroll container silently truncated pagination). The PDF export button
// now calls buildCrossSectionPdf(), which draws an entirely separate,
// purpose-built PDF - but it reuses the EXACT SAME chart-drawing function
// (ProfileChartRenderer.js) the preview below uses, so what the user reviews
// on screen and what ends up in the PDF can never visually disagree.

function metaItem(label, value){
	return `
		<div>
			<div style="font-size:9px; text-transform:uppercase; letter-spacing:0.04em; color:#888;">${label}</div>
			<div style="font-size:12px; font-weight:600; color:#222;">${value}</div>
		</div>
	`;
}

// Same dark-modal error convention as VolumeResultPanel.js's own
// showVolumeErrorPanel() - a native alert() would look out of place next to
// every other Digitalisering dialog and blocks the JS thread differently.
// dm (valgfri): findes en vært (viewer.pvUi), vises fejlen som almindelig host-besked (centreret, Luk); ellers DOM-dialogen (standalone).
export function showCrossSectionErrorPanel(message, dm){
	if(dm && hostDialogAvailable(dm.viewer)){
		requestHostConfirm(dm.viewer, {kind: "message", size: "s", title: t("Tværprofilrapport"), text: htmlMessageToText(message)});
		return;
	}
	const {overlay, box} = createDialogOverlay(dm, 340, {center: true});
	fillDialog(box, {
		title: t("Tværprofilrapport"),
		body: `<p class="pvp-desc" style="color:inherit">${message}</p>`,
		actions: pvpButton('ok', t("Luk"), 'primary'),
	});
	const close = () => overlay.isConnected && document.body.removeChild(overlay);
	box.querySelector('[data-action="ok"]').addEventListener("click", close);
	box.setAttribute("tabindex", "-1");
	box.addEventListener("keydown", (e) => { if(e.key === "Escape"){ e.stopPropagation(); close(); } });
	box.focus();
}

// Builds the report BODY - header/branding, metadata, the shared "positiv
// retning"-explanation (task section 11: stated ONCE here, not repeated per
// chart), the overview image, and the chart grid. Chart canvases are drawn
// live into the DOM (not converted to a data-URL <img>) so the on-screen
// preview is genuinely the same pixels ProfileChartRenderer.js produces.
function buildReportBody({stations, overviewCanvas, meta}){
	const root = document.createElement("div");
	root.style.cssText = "background:#f4f4f4; color:#111; padding:14px; border-radius:4px;";

	const generatedAt = formatGeneratedAt();

	const header = document.createElement("div");
	header.style.cssText = "display:flex; justify-content:space-between; align-items:flex-start; border-bottom:3px solid #2f8fd6; padding-bottom:10px; margin-bottom:10px;";
	header.innerHTML = `
		<div>
			<div style="font-size:20px; font-weight:bold; color:#111;">${t("Tværprofilrapport")}</div>
			<div style="font-size:13px; color:#444; margin-top:3px;">${meta.featureName}</div>
		</div>
		<div style="display:flex; align-items:center; gap:12px;">
			<div style="font-size:10.5px; color:#888; text-align:right;">${t("Punktsky-baseret tværprofilanalyse")}</div>
			<img src="${REPORT_LOGO_DATA_URI}" alt="${t("IT_thirty four")}" style="width:56px; height:56px; display:block;">
		</div>
	`;

	// task section 4: only fields this tool genuinely knows are shown -
	// Datakilde/Koordinatsystem are omitted rather than shown as "ukendt"
	// when the source data doesn't declare them (see crossSectionTool.js's
	// own doc comment on `pointcloud.projection`).
	const metaItemsHtml = [
		metaItem(t("Genereret"), generatedAt),
		meta.dataSource ? metaItem(t("Datakilde"), meta.dataSource) : "",
		meta.coordinateSystem ? metaItem(t("Koordinatsystem"), meta.coordinateSystem) : "",
		metaItem(t("Profilinterval"), `${formatNumber(meta.interval, 2)} m`),
		metaItem(t("Profillængde"), `${formatNumber(meta.profileLength, 2)} m`),
		metaItem(t("Profilbånd"), `${formatNumber(meta.band, 2)} m`),
		metaItem(t("Antal profiler"), String(stations.length)),
	].join("");

	const metaGrid = document.createElement("div");
	metaGrid.style.cssText = "display:grid; grid-template-columns: repeat(4, 1fr); gap:8px 18px; margin-bottom:8px; padding-bottom:12px; border-bottom:1px solid #ddd;";
	metaGrid.innerHTML = metaItemsHtml;

	const explanation = document.createElement("div");
	explanation.style.cssText = "font-size:11px; font-style:italic; color:#666; margin-bottom:14px;";
	explanation.textContent = t("Positiv retning er mod venstre set i forløbsretningen.");

	overviewCanvas.style.cssText = "width:100%; height:auto; display:block; border:1px solid #ccc; border-radius:4px; margin-bottom:16px;";

	const grid = document.createElement("div");
	grid.style.cssText = "display:grid; grid-template-columns: repeat(3, 1fr); gap:10px;";
	for(const station of stations){
		const {canvas} = renderProfileChartCanvas(station, meta.profileLength);
		canvas.style.cssText = "width:100%; height:auto; display:block;";
		const card = document.createElement("div");
		card.style.cssText = "border:1px solid #ccc; border-radius:4px; padding:6px; background:#fff;";
		card.appendChild(canvas);
		grid.appendChild(card);
	}

	root.appendChild(header);
	root.appendChild(metaGrid);
	root.appendChild(explanation);
	root.appendChild(overviewCanvas);
	root.appendChild(grid);
	return root;
}

// Træk i `handle` flytter `box`. Boksen lå centreret i et flex-lag; ved første træk lægges den fast på sin nuværende plads
// (position:fixed), og derefter følger den musen - klemt, så titellinjen altid ligger inden for vinduet.
function makeDraggable(box, handle){
	let drag = null;

	const onMove = (e) => {
		if(!drag) return;
		const width = box.offsetWidth;
		const left = Math.min(Math.max(e.clientX - drag.dx, 40 - width), window.innerWidth - 40);
		const top = Math.min(Math.max(e.clientY - drag.dy, 0), window.innerHeight - 40);
		box.style.left = `${left}px`;
		box.style.top = `${top}px`;
	};
	const onUp = () => {
		drag = null;
		window.removeEventListener("mousemove", onMove);
		window.removeEventListener("mouseup", onUp);
	};

	handle.addEventListener("mousedown", (e) => {
		if(e.button !== 0) return;
		const rect = box.getBoundingClientRect();
		if(box.style.position !== "fixed"){
			box.style.width = `${rect.width}px`;
			box.style.position = "fixed";
			box.style.margin = "0";
			box.style.left = `${rect.left}px`;
			box.style.top = `${rect.top}px`;
		}
		drag = {dx: e.clientX - rect.left, dy: e.clientY - rect.top};
		window.addEventListener("mousemove", onMove);
		window.addEventListener("mouseup", onUp);
		e.preventDefault();
	});
}

// {stations: [{name, profilePoints}], overviewCanvas, meta: {featureName,
// interval, profileLength, band}}
export function showCrossSectionReport({stations, overviewCanvas, meta}){
	// Resultat-/informationsdialog: centreret med almindelig modal dæmpning (ikke top-left tool-placering) på det fælles
	// Potree-dialogskin. Bredden er rapportens egen (op til 1200 px), men aldrig bredere end viewporten; header og actions
	// bliver stående, brødteksten ruller (createDialogOverlay/fillDialog).
	const {overlay, box} = createDialogOverlay(null, 1200, {center: true});
	fillDialog(box, {
		title: t("Tværprofilrapport - {0}", meta.featureName),
		actions: pvpButton('export', t("Eksportér til PDF"), 'primary') + pvpButton('close', t("Luk")),
	});
	box.querySelector('.pvp-body').appendChild(buildReportBody({stations, overviewCanvas, meta}));
	const exportButton = box.querySelector('[data-action="export"]');

	const close = () => overlay.isConnected && document.body.removeChild(overlay);
	box.querySelector('[data-action="close"]').addEventListener("click", close);

	// Rapporten er et flytbart vindue (træk i titellinjen) med et kryds i højre hjørne - ikke en modal, der dæmper og blokerer
	// 3D-vinduet: oversigten bag den kan stadig bruges, og klik uden for lukker den ikke.
	overlay.style.background = "transparent";
	overlay.style.pointerEvents = "none";
	box.style.pointerEvents = "auto";
	box.style.boxShadow = "0 8px 32px rgba(0,0,0,0.45)";
	const head = box.querySelector(".pvp-head");
	head.style.position = "relative";
	head.style.cursor = "move";
	head.style.userSelect = "none";
	head.style.paddingRight = "48px";
	const closeX = document.createElement("button");
	closeX.type = "button";
	closeX.title = t("Luk");
	closeX.setAttribute("aria-label", t("Luk"));
	closeX.textContent = "\u00d7";
	closeX.style.cssText = "position:absolute; top:50%; right:10px; transform:translateY(-50%); width:30px; height:30px; border:none; border-radius:4px; background:transparent; color:inherit; font-size:24px; line-height:1; cursor:pointer; opacity:0.75;";
	closeX.addEventListener("mouseenter", () => { closeX.style.opacity = "1"; closeX.style.background = "rgba(128,128,128,0.25)"; });
	closeX.addEventListener("mouseleave", () => { closeX.style.opacity = "0.75"; closeX.style.background = "transparent"; });
	closeX.addEventListener("mousedown", (e) => e.stopPropagation());
	closeX.addEventListener("click", close);
	head.appendChild(closeX);
	makeDraggable(box, head);
	box.setAttribute("tabindex", "-1");
	box.addEventListener("keydown", (e) => { if(e.key === "Escape"){ e.stopPropagation(); close(); } });
	box.focus();

	// buildCrossSectionPdf() draws an entirely new, purpose-built PDF (see
	// its own doc comment for why this replaced window.print()) and re-runs
	// the chart layout at PDF resolution - it does not reuse the specific
	// <canvas> elements already in this preview, so the button stays
	// responsive even for a large profile count without re-rendering the
	// on-screen grid.
	exportButton.addEventListener("click", () => {
		const originalLabel = exportButton.textContent;
		exportButton.disabled = true;
		exportButton.textContent = t("Genererer PDF…");
		setTimeout(() => {
			try{
				buildCrossSectionPdf({stations, overviewCanvas, meta});
			}finally{
				exportButton.disabled = false;
				exportButton.textContent = originalLabel;
			}
		}, 0);
	});
}
